import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useParams } from "react-router-dom";
import axios from "axios";
import Button from "../../../../common/Button";
import { updateInterventionAssessment } from "../../../../../redux/modules/intervention/actions";
import { findAvailableStep } from "../../../text/data";
import { AmrDisplayKind, formatAmrForDisplay } from "../../../clinical-services/service-steps/medication/data";
import { isAnnualMedicationReviewIntervention, getAmrMedicationRowForIntervention, PrescriberSuggestionOptions} from "../data";
import NewToolTip from "../../../../common/New-ToolTip";
import PharmacistTipTapEditor from "../../../../common/PharmacistTipTapEditor";
import { useMonographStatuses } from "../../../../../hooks/useMonographStatuses";

const RxVigilanceDinSearch = ({ din }) => {
  const { data: monographStatusByIdentifier } = useMonographStatuses([din]);

  const vigilanceEnv = import.meta.env.VITE_VIGILANCE_ENV || "int";
  const rxVigilanceBaseUrl = `https://rx.${vigilanceEnv}.vigilance.ca`;
  const rxVigilanceAuthUrl = `https://rxauth.${vigilanceEnv}.vigilance.ca`;

  const monographStatus =
    din != null ? monographStatusByIdentifier[String(din)] : null;
  const hasMonograph = monographStatus?.hasMonograph;
  const monographId = monographStatus?.monographId;

  const handleClick = async () => {
    if (!hasMonograph || !monographId) {
      return;
    }

    try {
      const tokenResponse = await axios.post(
        `${rxVigilanceAuthUrl}/token`,
        new URLSearchParams({
          client_id: import.meta.env.VITE_APP_VIGILANCE_ID,
          client_secret: import.meta.env.VITE_APP_VIGILANCE_SECRET,
          user_id: "REEL01",
          grant_type: "client_credentials",
        }),
        { headers: { "Content-Type": "application/x-www-form-urlencoded" } }
      );

      const token = tokenResponse.data.access_token;

      // Some Rx-Vigilance endpoints require a POST + hidden token,
      // so we submit a hidden form to open in a new tab.
      const form = document.createElement("form");
      form.method = "POST";
      form.action = `${rxVigilanceBaseUrl}/module/mono/en/${monographId}.htm`;
      form.target = "_blank";

      const tokenInput = document.createElement("input");
      tokenInput.type = "hidden";
      tokenInput.name = "token";
      tokenInput.value = token;

      form.appendChild(tokenInput);
      document.body.appendChild(form);
      form.submit();
      document.body.removeChild(form);
    } catch (error) {
      console.error("Failed to open monograph:", error);
    }
  };

  const tooltipId = `monograph-${din}`;

  return (
    <>
      <img
        src="/icons/rx-vigilance-icon.svg"
        alt="DIN Monograph"
        data-tooltip-id={tooltipId}
        className="cursor-pointer w-10 min-w-[40px]"
        onClick={handleClick}
      />
      {(!din || (monographStatus && !hasMonograph)) && (
        <NewToolTip
          id={tooltipId}
          place="bottom"
          render={() => (
            <div className="flex flex-col max-w-md p-2 bg-gray-20">
              <p className="text-sm text-teal-600">No monograph available</p>
            </div>
          )}
        />
      )}
    </>
  );
};

const PrescriberFollowUp = ({ intervention, currentInterventionSteps, currentStep: activeStep = 4 }) => {
  const dispatch = useDispatch();
  const { interventionId } = useParams();

  // Get patient medications from Redux state
  const { medications } = useSelector((state) => state.clinicalService);
  const clinicalServiceObj = useSelector((state) => state.intervention.clinicalServiceObj);

  /** AMR row by medicationId — same source as InterventionDetails (needed for adherence heading). */
  const amrMedicationRow =
    intervention && isAnnualMedicationReviewIntervention(intervention)
      ? getAmrMedicationRowForIntervention(clinicalServiceObj, intervention) : null;
  const amrAdherenceForHeading =
    amrMedicationRow?.adherence ??
    amrMedicationRow?.Adherence ??
    amrMedicationRow?.consumption ??
    amrMedicationRow?.Consumption ??
    null;
  const adherenceStatusCardTitle = formatAmrForDisplay(
    AmrDisplayKind.AssessmentStatusHeading,
    amrAdherenceForHeading
  ).replace(/^Assessment Status/, "Adherence Status");

  const medicationIdForMonograph =
    intervention?.interventionAssessment?.interventionAssessmentForm
      ?.interventionDetails?.medicationId;

  const dinForMonograph =
    medicationIdForMonograph && Array.isArray(medications) && medications.length > 0
      ? (medications.find((m) => m.id === medicationIdForMonograph)?.medicationGenCode ??
          medications.find((m) => m.id === medicationIdForMonograph)?.MedicationGenCode ??
          medications.find((m) => m.id === medicationIdForMonograph)?.din ??
          medications.find((m) => m.id === medicationIdForMonograph)?.DIN ??
          null)
      : null;

  // State management for form data - matching backend structure
  const [pharmacistSuggestions, setPharmacistSuggestions] = useState("");
  const [prescriberSuggestions, setPrescriberSuggestions] = useState([]);
  const [suggestFollowUp, setSuggestFollowUp] = useState(false);
  const [pharmacistNotesToolbarHost, setPharmacistNotesToolbarHost] = useState(null);

  // Fallback data for UI development
  const fallbackMedicationName = "N/A";
  const staticPrescriberSuggestion = ["noChange"];
  const staticSuggestFollowUp = true;
  const staticPharmacistNotes =
    "Patient is experiencing mild dizziness with current medication. Recommend monitoring blood pressure closely and consider dose adjustment if symptoms persist. Patient should report any severe side effects immediately.";

  // Initialize state from intervention data
  useEffect(() => {
    if (
      intervention?.interventionAssessment?.interventionAssessmentForm
        ?.prescriberFollowUp
    ) {
      const prescriberFollowUp =
        intervention.interventionAssessment.interventionAssessmentForm
          .prescriberFollowUp;

      setPharmacistSuggestions(prescriberFollowUp.pharmacistSuggestions || "");
      // Backend returns string; normalize to single-selection array for UI
      const prescriberSuggestionValue =
        prescriberFollowUp.prescriberSuggestions || "";
      if (Array.isArray(prescriberSuggestionValue)) {
        setPrescriberSuggestions(prescriberSuggestionValue);
      } else if (
        typeof prescriberSuggestionValue === "string" &&
        prescriberSuggestionValue.length > 0
      ) {
        setPrescriberSuggestions([prescriberSuggestionValue]);
      } else {
        setPrescriberSuggestions([]);
      }
      setSuggestFollowUp(prescriberFollowUp.suggestFollowUp || false);
    }
  }, [intervention]);

  // Options for prescriber suggestions (centralized)
  const prescriberSuggestionOptions = PrescriberSuggestionOptions;

  // Checkbox change handler - single selection only
  const handlePrescriberSuggestionChange = (e) => {
    if (e.target.checked) {
      // If checking a new option, clear all others and select only this one
      setPrescriberSuggestions([e.target.name]);
    } else {
      // If unchecking, clear the selection
      setPrescriberSuggestions([]);
    }
  };

  // Helper function to get original prescription from medications list
  const getOriginalPrescriptionDisplayName = () => {
    const details =
      intervention?.interventionAssessment?.interventionAssessmentForm
        ?.interventionDetails;

    // Prefer saved intervention details (works for Minor Ailment and outside-service flows)
    if (details) {
      const detailMedicationName = details.medicationName || details.name || "";
      const detailRoute = details.route || "";
      const detailDosage = details.dosage || "";
      const detailForm = details.form || "";
      const detailFrequency = details.frequency || "";

      const detailsDisplay =
        `${detailMedicationName} ${detailRoute} [${detailDosage} ${detailForm} ${detailFrequency}]`
          .replace(/\s+/g, " ")
          .trim();

      if (detailsDisplay && detailsDisplay !== "[]") {
        return detailsDisplay;
      }
    }

    // Get medicationId from intervention details
    const medicationId =
      intervention?.interventionAssessment?.interventionAssessmentForm
        ?.interventionDetails?.medicationId;

    if (medicationId && medications && medications.length > 0) {
      // Find the medication in patient's medications array
      const medication = medications.find((med) => med.id === medicationId);

      if (medication) {
        // Build display name from medication data
        const medicationName =
          medication.medicationName || medication.name || "Unknown Medication";
        const route = medication.route || "";
        const dosage = medication.dosage || "";
        const form = medication.form || "";
        const frequency = medication.frequency || "";

        const displayName =
          `${medicationName} ${route} [${dosage} ${form} ${frequency}]`.trim();
        return displayName;
      }
    }

    return fallbackMedicationName;
  };

  // Helper function to get current regimen from intervention details
  const getCurrentRegimenDisplayName = () => {
    if (
      intervention?.interventionAssessment?.interventionAssessmentForm
        ?.interventionDetails
    ) {
      const details =
        intervention.interventionAssessment.interventionAssessmentForm
          .interventionDetails;
      const currentRegimenName = `${
        details.medicationName || "Unknown Medication"
      } ${details.route || ""} [${details.dosage || ""} ${details.form || ""} ${
        details.frequency || ""
      }]`.trim();
      return currentRegimenName;
    }

    return fallbackMedicationName;
  };

  // Navigation functions
  const onNext = async () => {
    try {
      // Find next available step using common function
      const currentStep =
        activeStep ||
        intervention?.interventionAssessment?.interventionAssessmentForm
          ?.currentStep ||
        4;
      const nextStep = findAvailableStep(
        currentStep,
        currentInterventionSteps,
        1
      );

      // Prepare the data to save
      const prescriberSuggestionsString =
        prescriberSuggestions && prescriberSuggestions.length > 0
          ? prescriberSuggestions[0]
          : "";
      const assessmentData = {
        currentStep: nextStep,
        prescriberFollowUp: {
          pharmacistSuggestions: pharmacistSuggestions,
          prescriberSuggestions: prescriberSuggestionsString,
          suggestFollowUp: suggestFollowUp,
        },
      };

      // Save the changes and navigate to next step
      await dispatch(
        updateInterventionAssessment(interventionId, assessmentData)
      );
    } catch (error) {
      console.error("Failed to update intervention step:", error);
    }
  };

  const onPrevious = async () => {
    try {
      // Find previous available step using common function
      const currentStep =
        activeStep ||
        intervention?.interventionAssessment?.interventionAssessmentForm
          ?.currentStep ||
        4;
      const previousStep = findAvailableStep(
        currentStep,
        currentInterventionSteps,
        -1
      );

      // Prepare the data to save
      const prescriberSuggestionsString =
        prescriberSuggestions && prescriberSuggestions.length > 0
          ? prescriberSuggestions[0]
          : "";
      const assessmentData = {
        currentStep: previousStep,
        prescriberFollowUp: {
          pharmacistSuggestions: pharmacistSuggestions,
          prescriberSuggestions: prescriberSuggestionsString,
          suggestFollowUp: suggestFollowUp,
        },
      };

      // Save the changes and navigate to previous step
      await dispatch(
        updateInterventionAssessment(interventionId, assessmentData)
      );
    } catch (error) {
      console.error("Failed to update intervention step:", error);
    }
  };

  // Determine if buttons should be enabled
  const canGoNext = prescriberSuggestions && prescriberSuggestions.length > 0;
  const canGoPrevious = true; // Can go back to step 3
  if (!intervention) {
    return (
      <div className="w-full h-full flex items-center justify-center bg-white">
        <div className="text-center">
          <div className="text-gray-400 text-xl font-medium mb-4">
            No Intervention Data
          </div>
          <div className="text-gray-500 text-base">
            Prescriber Follow Up information is not available.
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="w-full max-h-fit overflow-x-hidden overflow-auto">
        <div className="w-full h-full flex flex-col py-3">
          <div className="flex flex-col xl:flex-row gap-8 flex-1 min-w-0">
            <div className="w-full xl:flex-1 min-w-0 h-[280px] rounded-[4px] border border-gray-100 bg-gray-10 flex flex-col box-border">
              <div className="w-full min-w-0 h-[72px] min-h-[72px] max-h-[72px] box-border gap-[10px] opacity-1 px-6 py-3 bg-gray-20 border border-gray-100 flex items-center justify-between">
                <h3 className="text-teal-300 font-semibold text-xl">
                  {adherenceStatusCardTitle}
                </h3>
              </div>

              <div className="flex-1 p-6">
                <div className="mb-4">
                  <div className="flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <div className="text-sm font-semibold text-gray-600 mb-1">
                        Original Prescription
                      </div>
                      <div className="font-bold text-gray-800">
                        {getOriginalPrescriptionDisplayName()}
                      </div>
                    </div>
                    <div className="flex-shrink-0">
                      <RxVigilanceDinSearch din={dinForMonograph} />
                    </div>
                  </div>
                </div>

                {isAnnualMedicationReviewIntervention(intervention) && (
                  <div className="mb-6">
                    <div className="text-sm font-semibold text-gray-600 mb-1">
                      Current Regimen
                    </div>
                    <div className="font-bold text-gray-800">
                      {getCurrentRegimenDisplayName()}
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="w-full xl:flex-1 min-w-0 h-[280px] rounded-[4px] border border-gray-100 bg-gray-10 flex flex-col box-border">
              <div className="w-full min-w-0 h-[72px] min-h-[72px] max-h-[72px] box-border gap-[10px] opacity-1 px-6 py-3 bg-gray-20 border border-gray-100 flex items-center justify-between">
                <h3 className="text-teal-300 font-semibold text-xl">
                  Suggestions for Prescriber
                </h3>
              </div>

              <div className="flex-1 px-6 py-3">
                <div className="w-full min-w-0 gap-[6px] opacity-1 ">
                  <div className="w-full min-w-0 h-[24px] gap-[4px] opacity-1 pt-[6px] pr-[10px] pb-[6px] pl-[10px] rounded-[4px] flex items-center justify-between mb-2">
                    <span className="text-teal-300 text-sm font-medium">
                      Select one response:
                    </span>
                  </div>

                  <div
                    className={`w-full min-w-0 h-[140px] gap-[10px] justify-center items-start flex flex-col opacity-1 py-3 pr-[77px] pl-[20px] rounded-[4px] border border-gray-100 ${
                      prescriberSuggestions.length === 0
                        ? "bg-blue-100"
                        : "bg-white"
                    }`}
                  >
                    <div className="flex flex-col gap-[10px]">
                      {prescriberSuggestionOptions.map((item, index) => (
                        <span
                          key={index}
                          className="flex flex-row text-body-md font-normal"
                        >
                          <input
                            className=""
                            type="checkbox"
                            id={index}
                            name={item.label}
                            checked={prescriberSuggestions?.includes(
                              item.label
                            )}
                            onChange={handlePrescriberSuggestionChange}
                          />
                          <label
                            htmlFor={index}
                            className={`ml-4 text-base font-normal ${
                              prescriberSuggestions?.includes(item.label)
                                ? "text-teal-300"
                                : "text-gray-700"
                            }`}
                          >
                            <span className="font-semibold">
                              {item.label.split(":")[0]}:
                            </span>{" "}
                            {item.label.split(":")[1]}
                          </label>
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Third Card: Pharmacist Recommendations */}
          <div className="mt-6 box-border flex h-[310px] min-h-0 w-full min-w-0 flex-col rounded-[4px] border border-gray-100 bg-gray-10">
            <div className="box-border flex h-[54px] min-h-[54px] max-h-[54px] w-full min-w-0 shrink-0 items-center justify-between gap-[10px] border border-gray-100 border-t-[1px] border-t-gray-100 border-t-l-[4px] border-t-r-[4px] bg-gray-20 px-6 py-3 opacity-1">
              <h3 className="min-w-0 flex-1 text-xl font-semibold text-teal-300">
                Pharmacist Recommendations{" "}
                <span className="font-normal">| Suggest new Rx changes</span>
              </h3>
              <div
                className="flex shrink-0 items-center"
                ref={setPharmacistNotesToolbarHost}
              />
            </div>

            <div className="flex min-h-0 flex-1 flex-col px-2 pb-2 pt-2">
              <PharmacistTipTapEditor
                value={pharmacistSuggestions}
                onChange={setPharmacistSuggestions}
                minHeight="0"
                className="flex min-h-0 flex-1 flex-col !bg-gray-10"
                hideOuterBorder
                hideInlineToolbar
                toolbarPortalContainer={pharmacistNotesToolbarHost}
                ariaLabel="Pharmacist recommendations for prescriber"
              />
            </div>
          </div>

          <div className="flex items-center pt-3">
            <input
              type="checkbox"
              id="suggestFollowUp"
              checked={suggestFollowUp}
              onChange={(e) => setSuggestFollowUp(e.target.checked)}
              className="mr-3 h-4 w-4 text-teal-300 focus:ring-teal-300 border-gray-300 rounded"
            />
            <label
              htmlFor="suggestFollowUp"
              className="text-base text-teal-300 cursor-pointer"
            >
              <span className="font-semibold">
                Pharmacist suggests follow up appointment with Primary Provider
              </span>
            </label>
          </div>
        </div>
      </div>

      {/* Navigation Buttons */}
      <div className="flex items-center justify-between space-x-4">
        <div className="w-full">{/* Progress indicator could go here */}</div>
        <div className="flex items-center gap-3">
          <Button
            type="primary-outline"
            text="Back"
            className="w-[200px] max-h-[52px] !rounded-[30px]"
            onClick={onPrevious}
            disabled={!canGoPrevious}
          />
          <Button
            type="primary"
            text="Next"
            className="w-[200px] max-h-[52px] !rounded-[30px]"
            onClick={onNext}
            disabled={!canGoNext}
          />
        </div>
      </div>
    </>
  );
};

export default PrescriberFollowUp;
